
  import { Vue, Component, Prop } from 'vue-property-decorator'
  import { Products } from '@one/types'
  import {
    Cart,
    Wishlist,
  } from '@one/types/dist/orderpath/app'
  import { CreateElement, VNode } from 'vue'
  import uuid from 'uuid-random'
  import { BindingHelpers } from 'vuex-class/lib/bindings'
  import { namespace } from 'vuex-class'

  const stocks: BindingHelpers = namespace('stocks')
  const products: BindingHelpers = namespace('products')

  export interface ProductQuantityAndResourceId {
    id?: string
    quantity: number
  }

  export interface WishlistInterface {
    id: string
    name: string
    timestamp: number
  }

  @Component
  export default class OneProductTileRightActions extends Vue {
    @stocks.Getter isEnoughProductWithBlockLabelInStock: any
    @products.Getter getProduct: any

    @Prop({
      required: true,
      type: String,
    })
    productId!: string

    @Prop({
      required: true,
      type: String,
    })
    unit!: string

    @Prop({
      required: true,
      type: Array,
    })
    carts!: Array<Cart.Responses.BasicCartInfo>

    @Prop({
      required: true,
      type: Array,
    })
    wishlists!: Array<Wishlist.Responses.BasicWishlist>

    @Prop({
      type: Boolean,
      default: true,
    })
    showAddToWishlistButton!: boolean

    @Prop({
      type: Boolean,
      required: false,
      default: false,
    })
    progress?: boolean

    @Prop({
      type: Boolean,
      required: false,
      default: false,
    })
    progressWishlist?: boolean

    @Prop({
      type: Boolean,
      required: false,
      default: true,
    })
    extended!: boolean

    quantityInOrderUnit: number = 1
    calculatedQuantity: number = 0

    get product(): Products.Product | null {
      return this.getProduct(this.productId)
    }

    created() {
      this.quantityInOrderUnit = this.product?.measurementUnits.quantityMin ?? 1
    }

    render(createElement: CreateElement): VNode {
      const btnWishlistId: string = `btn-wishlist-${uuid()}`
      const btnCartId: string = `btn-add-cart-${uuid()}`
      let hasQuantityError: boolean = false
      let currentQuantity: number = this.product?.measurementUnits.quantityMin ?? 1
      const disabledAddToCart = (): boolean => {
        // TODO: isEnoughProductWithBlockLabelInStock return invalid (negated) value
        return (
          this.product &&
          this.isEnoughProductWithBlockLabelInStock(
            currentQuantity,
            this.product.labels,
            this.product.id,
          )
        )
      }
      const enabledCalculation = (): boolean => {
        return (
          !!this.product?.measurementUnits?.contentUnitDescription &&
          !!this.product?.measurementUnits?.packingQuantity &&
          this.product?.labels.includes('tile_calculator')
        )
      }
      const changeValue = (event: number) => {
        this.quantityInOrderUnit = event
        currentQuantity = Math.abs(event)
        this.$emit('quantity-changed', Math.abs(event))
      }
      const onContentQuantityChanged = (event: number) => {
        if (this.product?.measurementUnits?.packingQuantity) {
          this.calculatedQuantity = Math.ceil(
            event / this.product?.measurementUnits?.packingQuantity,
          )
        }
      }
      const onQuantityError = (e: boolean) => {
        hasQuantityError = e
      }
      const addToCart = (id?: string) => {
        if (!hasQuantityError) {
          this.$emit('add-to-cart', {
            id,
            quantity: currentQuantity,
          })
        }
      }
      const onAddToWishlist = (wishlist?: WishlistInterface) => {
        if (!hasQuantityError) {
          this.$emit('add-to-wishlist', {
            wishlist,
            quantity: currentQuantity,
          })
        }
      }
      const generateInput = () =>
        createElement('one-input-tooltip', {
          props: {
            amountSyncTrigger: btnCartId,
            productId: this.productId,
            unit: this.product?.measurementUnits.orderUnitDescription,
            'initial-quantity': this.product?.measurementUnits.quantityMin,
            'measurement-units': this.product?.measurementUnits,
            calculatedQuantity: this.calculatedQuantity,
          },
          on: {
            'qty-error': (e: any) => onQuantityError(e),
            'change-value': (e: any) => changeValue(e),
            'input-value': (e: any) => changeValue(e),
          },
        })
      const generateInputInfo = () =>
        createElement('one-quantity-and-interval-info', {
          props: {
            'quantity-interval': this.product?.measurementUnits.quantityInterval || 1,
            'quantity-min': this.product?.measurementUnits.quantityMin || 1,
          },
        })
      const generateContentUnitInput = () =>
        createElement('one-content-unit-input', {
          props: {
            unit: this.product?.measurementUnits?.contentUnitDescription,
            packingQuantity: this.product?.measurementUnits?.packingQuantity,
            quantityInOrderUnit: Number(this.quantityInOrderUnit),
          },
          on: {
            'content-quantity-changed': (e: any) => onContentQuantityChanged(e),
          },
        })
      const generateInputInfoContainer = () =>
        createElement(
          'div',
          {
            class: 'one-quantity-and-interval-info__container',
          },
          [generateInputInfo()],
        )
      const generateInputContainer = () => {
        return createElement(
          'div',
          {
            class: this.extended ? ['input-container px-1'] : ['input-on-hover'],
          },
          [generateInput()],
        )
      }
      const generateContentUnitInputContainer = () => {
        if (enabledCalculation()) {
          return createElement(
            'div',
            {
              class: 'input-container px-1',
            },
            [generateContentUnitInput()],
          )
        }
      }
      const generateTooltipError = () => {
        if (!this.carts || this.carts.length < 1) {
          return null
        }
        return createElement(
          'b-tooltip',
          {
            class: ['tooltip'],
            props: {
              disabled: !disabledAddToCart(),
              target: `${btnCartId}`,
              placement: 'bottom',
            },
          },
          [this.$t('product_page.insufficient_quantity_stock') as string],
        )
      }
      const generateAddToWishlist = () => {
        if (!this.wishlists || this.wishlists.length < 1 || !this.showAddToWishlistButton) {
          return null
        }
        return [
          createElement(
            'div',
            {
              class: ['mx-2', 'p-0', 'btn-wishlist', hasQuantityError && 'disabled-btn'],
              attrs: {
                id: `${btnWishlistId}`,
              },
            },
            [
              createElement('one-product-dropdown-add-to-wishlist', {
                props: {
                  wishlists: this.wishlists,
                  progress: this.progressWishlist,
                  productId: this.productId,
                },
                on: {
                  'add-to-wishlist': (e: any) => onAddToWishlist(e),
                },
              }),
            ],
          ),
          createElement(
            'b-tooltip',
            {
              attrs: {
                target: `${btnWishlistId}`,
                triggers: ['hover'],
              },
            },
            [this.$t('product_page.add_to_wishlist') as string],
          ),
        ]
      }
      const generateAddToCart = () => {
        if (!this.carts || this.carts.length < 1) {
          return null
        }
        return [
          createElement(
            'div',
            {
              class: {
                'ml-2': !this.showAddToWishlistButton,
              },
              attrs: {
                id: `${btnCartId}`,
              },
            },
            [
              createElement(
                'one-product-dropdown-add-to-cart',
                {
                  class: {
                    'disabled-btn': hasQuantityError || disabledAddToCart(),
                  },
                  props: {
                    carts: this.carts,
                    progress: this.progress,
                    disabled: hasQuantityError || disabledAddToCart(),
                    productId: this.productId,
                  },
                  on: {
                    'add-to-cart': (e: any) => addToCart(e),
                  },
                },
                [this.extended ? (this.$t('product_page.add_to_cart') as string) : ''],
              ),
            ],
          ),
          createElement(
            'b-tooltip',
            {
              attrs: {
                target: `${btnCartId}`,
                triggers: ['hover'],
              },
            },
            [this.$t('product_page.add_to_cart') as string],
          ),
        ]
      }
      return createElement(
        'div',
        {
          class: [
            'one-product-tile-right-actions',
            'border-bottom-xs',
            'py-2',
            'actions-container',
          ],
          props: {
            'align-v': 'center',
          },
        },
        [
          createElement('b-col', {}, [
            createElement(
              'div',
              {
                class: ['d-flex', 'justify-content-end', 'align-items-center'],
              },
              [
                generateContentUnitInputContainer(),
                enabledCalculation() ? createElement('p', { class: ['pb-1'] }, ['=']) : '',
                generateInputContainer(),
                generateTooltipError(),
                generateAddToWishlist(),
                generateAddToCart(),
              ],
            ),
            createElement(
              'div',
              {
                class: ['d-flex', 'justify-content-end', 'align-items-center'],
              },
              [generateInputInfoContainer()],
            ),
          ]),
        ],
      )
    }
  }
